// Flat-key bundle helpers for module-level i18n.
//
// Module i18n files (e.g. `modules/fleet/i18n/sr-Latn.json`) ship a flat dict
// keyed on dotted paths. The convention deliberately allows both:
//
//     "fleet.machine":             "Mehanizacija"          ← entity label
//     "fleet.machine.interni_broj": "Interni broj"         ← field label
//
// `next-intl` stores messages as a NESTED tree, so it can't represent both
// of those paths simultaneously (the latter needs `fleet.machine` to be an
// object, the former needs it to be a string). Forcing every module author
// to introduce a sentinel suffix (e.g. `fleet.machine._label`) would be
// noisy and migration-heavy.
//
// Instead we keep module bundles as a flat `Record<string, string>` and
// resolve them through `useFlatTranslations()` (see `flat-keys-provider.tsx`).
// `flatToNested` is retained only for unit tests / consumers that want a
// strictly-nested tree — it now uses a sentinel suffix (`__value`) so the
// same flat dict can survive the round-trip without colliding.

import type { MessageTree } from "./provider";

/**
 * Sentinel used by {@link flatToNested} when a key value would otherwise
 * collide with a sub-tree at the same path. The next-intl provider does not
 * understand this sentinel; it exists purely so the flat→nested transform is
 * total (and the helper is round-trippable for tests).
 */
const VALUE_SENTINEL = "__value";

/**
 * Convert a flat dict of dotted keys into a nested tree. When a key would
 * otherwise collide with a sub-tree at the same path (e.g.
 * `"fleet.machine"` and `"fleet.machine.x"`), the leaf value is stored under
 * the {@link VALUE_SENTINEL} child so both representations survive.
 *
 * For the substrate's main module-bundle path we keep the dict flat and use
 * `useFlatTranslations` for lookups — this helper exists so consumers that
 * specifically want a nested tree (e.g. unit tests) can still get one.
 *
 * Empty keys (`""`) and keys with empty segments (`"a..b"`) are rejected.
 */
export function flatToNested(flat: Record<string, string>): MessageTree {
  const out: MessageTree = {};
  for (const [key, value] of Object.entries(flat)) {
    if (key === "") {
      throw new Error("flatToNested: empty key not allowed");
    }
    const segments = key.split(".");
    if (segments.some((s) => s === "")) {
      throw new Error(`flatToNested: empty segment in key "${key}"`);
    }
    let cursor: MessageTree = out;
    for (let i = 0; i < segments.length - 1; i++) {
      const seg = segments[i] as string;
      const existing = cursor[seg];
      if (existing === undefined) {
        const next: MessageTree = {};
        cursor[seg] = next;
        cursor = next;
      } else if (typeof existing === "string") {
        // Convert the leaf to `{ __value: existing }` so the sub-tree below
        // can attach as siblings.
        const promoted: MessageTree = { [VALUE_SENTINEL]: existing };
        cursor[seg] = promoted;
        cursor = promoted;
      } else {
        cursor = existing;
      }
    }
    const last = segments[segments.length - 1] as string;
    const slot = cursor[last];
    if (slot === undefined) {
      cursor[last] = value;
    } else if (typeof slot === "string") {
      // Two flat keys collide on the same exact path; right-most wins.
      cursor[last] = value;
    } else {
      // Existing slot is a sub-tree — store the leaf under the sentinel.
      slot[VALUE_SENTINEL] = value;
    }
  }
  return out;
}

/**
 * Deep-merge any number of {@link MessageTree}s. Right-most wins on string
 * collisions. Sub-trees merge recursively rather than replacing, so a partial
 * override doesn't drop sibling keys.
 */
export function mergeMessageTrees(
  ...trees: readonly MessageTree[]
): MessageTree {
  const out: MessageTree = {};
  for (const tree of trees) {
    mergeInto(out, tree);
  }
  return out;
}

function mergeInto(target: MessageTree, source: MessageTree): void {
  for (const [k, v] of Object.entries(source)) {
    const existing = target[k];
    if (typeof v === "string") {
      target[k] = v;
    } else if (existing === undefined || typeof existing === "string") {
      target[k] = mergeMessageTrees(v);
    } else {
      mergeInto(existing, v);
    }
  }
}

/** Sentinel used by {@link flatToNested}. Exposed so consumers that walk the
 *  nested tree manually can recognise wrapped leaf values. */
export const FLAT_TO_NESTED_VALUE_SENTINEL = VALUE_SENTINEL;
